import { Button } from '@acpaas-ui/react-components';
import _ from 'lodash';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../actions/fetch.actions';
import { resourceUpdate } from '../actions/resources.actions';
import AttachmentsList from '../components/attachments/AttachmentsList';
import Datepicker from '../components/forms/Datepicker';
import TextArea from '../components/forms/TextArea';
import TextField from '../components/forms/TextField';
import { MODALS } from '../constants/modals.constants';
import { ATTACHMENT_TYPES } from '../constants/settings.constants';
import dateValidators from '../form-validators/date.validators';
import { useAttachmentCRUD } from '../hooks/useAttachmentCRUD.hook';
import { useResourceInput } from '../hooks/useResourceInput';
import { MEASURE_MOMENTS_RESOURCE } from '../resources/measure.moments.resource';
import procedureService from '../services/api/procedure.service';
import AttachmentDetailsModal from './AttachmentDetailsModal';
import AttachmentUploadModal from './AttachmentUploadModal';
import DeleteWarningModal from './DeleteWarningModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';

const MeasureMomentDetailsModal = ({
  write,
  isOpen,
  onClose,
  onDelete,
  onUpdated,
  onAttachmentsRefetch,
  form,
  dossierId,
  goalId
}) => {
  const dispatch = useDispatch();
  const attachmentCRUD = useAttachmentCRUD(dossierId);
  const { data, input, load, reset, isSubmitting, error, submitStart, submitOk, submitError } = form;
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();
      procedureService.updateIndicatorMeasureMoment(
        dossierId,
        goalId,
        data.indicatorId,
        data.id,
        { [field]: value },
        {
          onSuccess: (resp) => {
            submitOk();
            input(value, field);
            onUpdated(value, field, data.indicatorId, data.id);
            dispatch(
              resourceUpdate(
                { key: `${MEASURE_MOMENTS_RESOURCE.key}_${data.indicatorId}` },
                'data',
                resp?.dossier?.procedure?.goals
                  ?.find((g) => g.id === goalId)
                  ?.indicators?.find((i) => i.id === data.indicatorId)?.measureMoments ?? []
              )
            );
            dispatch(fetchReset({ key: `${MEASURE_MOMENTS_RESOURCE.key}_${data.indicatorId}` }));
            options.onSuccess();
          },
          onError: (message) => {
            submitError(message);
            options.onError(message);
          }
        }
      );
    },
    validators: {
      date: dateValidators.dateTextField
    },
    onCancel: reset
  };
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    reset();
    load(MODALS.MEASURE_MOMENT_DETAILS.initialState);
    onClose && onClose();
  };

  const handleAttachmentAdd = (attachmentData) => {
    setIsUploadingAttachment(true);
    return attachmentCRUD.onAttachmentAdd(attachmentData, {
      onSuccess: (resp) => {
        input(
          _.uniqBy([...data.attachments, resp], (a) => a.id),
          'attachments'
        );
        setIsUploadingAttachment(false);
      },
      onError: () => {
        setIsUploadingAttachment(false);
      }
    });
  };

  const handleAttachmentDelete = (onError, onSuccess) => {
    setIsUploadingAttachment(true);
    attachmentCRUD.onAttachmentDelete(
      (error) => {
        onError(error);
        setIsUploadingAttachment(false);
      },
      () => {
        onSuccess();
        input(
          data.attachments?.filter((a) => a.id !== attachmentCRUD.attachmentDetailForm.data.id),
          'attachments'
        );
        setIsUploadingAttachment(false);
      }
    );
  };

  return (
    <Modal
      className="spo-o-measure-moment-create-modal"
      title={
        <ModalTitle
          hasError={!!error}
          title="Meetmoment"
          subtitle="Voeg hieronder meetmomenten toe die tijdens het traject zullen plaatsvinden of reeds plaatsgevonden hebben."
        />
      }
      isOpen={isOpen}
      onClose={handleClose}
      disableConfirm
      disableCancel
      extraFooterActions={
        write &&
        onDelete && (
          <Button type="danger" outline onClick={onDelete}>
            Verwijderen
          </Button>
        )
      }
      isSubmitting={isSubmitting}
    >
      <div className="row u-margin-bottom">
        <div className="col-xs-12 col-md-6">
          <Datepicker
            {...useResourceInput('date', resource, '', { ignoreTouched: true })}
            label="Datum"
            required
            readOnly={!write}
          />
        </div>
      </div>
      <div className="row u-margin-bottom">
        <div className="col-xs-12">
          <TextArea
            size="small"
            label="Vaststelling"
            {...useResourceInput('determination', resource)}
            readOnly={!write}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-lg">
        <div className="col-xs-12">
          <TextField label="Methode" {...useResourceInput('method', resource)} readOnly={!write} />
        </div>
      </div>
      <AttachmentsList
        title="Bijlagen"
        attachments={data.attachments}
        onOpen={attachmentCRUD.onAttachmentOpen}
        onUpload={
          write ? () => attachmentCRUD.onAttachmentAddOpen(data.id, ATTACHMENT_TYPES.MEASURE_MOMENT) : undefined
        }
        loading={isUploadingAttachment}
      />
      <AttachmentUploadModal {...attachmentCRUD.uploadModal} onConfirm={handleAttachmentAdd} />
      <AttachmentDetailsModal
        {...attachmentCRUD.detailsModal}
        write={write}
        form={attachmentCRUD.attachmentDetailForm}
        dossierId={dossierId}
        onUpdate={attachmentCRUD.onAttachmentUpdate}
        onUpdateSuccess={onAttachmentsRefetch}
        onDelete={attachmentCRUD.deleteModal.onOpen}
      />
      {attachmentCRUD.deleteModal.isOpen && (
        <DeleteWarningModal
          {...attachmentCRUD.deleteModal}
          warningText="Weet je zeker dat je deze bijlage wilt verwijderen?"
          onConfirm={handleAttachmentDelete}
        />
      )}
    </Modal>
  );
};

export default MeasureMomentDetailsModal;
